/**
 * Modal dialog for setting a Sell action on a player in scenario mode.
 *
 * Shows a Sale Price input using MoneyInput. Supports create mode
 * (Cancel + Confirm) and edit mode (Cancel Sell + Close + Update).
 * @module features/squad-planning/components/SellActionModal
 */

import React, { memo } from "react";

import LiveImpactPanel from "@/features/financial-compliance/components/molecules/LiveImpactPanel";
import MoneyInput from "@/components/molecules/MoneyInput";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Heading } from "@/components/atoms/Heading";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";

import type { LiveImpactModel } from "@/features/financial-compliance/types";

/** Props for {@link SellActionModalView} */
export interface SellActionModalViewProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Player name for the title */
  readonly playerName: string;
  /** Current sale price value in raw units */
  readonly salePrice: number | null;
  /** Called when sale price changes via MoneyInput typing and confirm */
  readonly onSalePriceChange: (value: number | null) => void;
  /** Called on confirm/update */
  readonly onConfirm: () => void;
  /** Called on cancel/close */
  readonly onCancel: () => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Whether the modal is in edit mode */
  readonly isEditMode?: boolean;
  /** Live Impact verdict for the scenario after this action (SE-7002) */
  readonly liveImpact: LiveImpactModel;
  /** Opens the full impact view for the current scenario */
  readonly onViewFullImpact: () => void;
}

/**
 * Renders the Sell action modal with a Sale Price MoneyInput.
 * @param props - Component props
 * @param props.isOpen - Whether the modal is visible
 * @param props.playerName - Player name shown in the heading
 * @param props.salePrice - Current sale price in raw units
 * @param props.onSalePriceChange - Handler for sale price updates
 * @param props.onConfirm - Handler for confirm/update button
 * @param props.onCancel - Handler for cancel/close button and backdrop
 * @param props.onCancelAction - Handler for "Cancel Sell" link (edit mode)
 * @param props.isEditMode - Whether in edit mode
 * @param props.liveImpact - Live Impact verdict for the whole scenario
 * @param props.onViewFullImpact - Opens the full impact view
 */
const SellActionModalView = ({
  isOpen,
  playerName,
  salePrice,
  onSalePriceChange,
  onConfirm,
  onCancel,
  onCancelAction,
  isEditMode = false,
  liveImpact,
  onViewFullImpact,
}: SellActionModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onCancel} size="md">
    <ModalBackdrop />
    <ModalContent className="p-6">
      <ModalHeader>
        <Heading variant="title-lg">
          Sell{" "}
          <Text variant="title" className="text-accent-primary">
            {playerName}
          </Text>
        </Heading>
      </ModalHeader>
      <ModalBody>
        <Stack className="gap-4">
          <Stack className="gap-1">
            <Text variant="body-strong" className="text-content-muted">
              Sale Price
            </Text>
            <MoneyInput
              value={salePrice}
              onConfirm={onSalePriceChange}
              onChange={onSalePriceChange}
              onCancel={onCancel}
              variant="expanded"
            />
            <Text
              variant="caption"
              testID="sell-sale-price-gating-note"
              className="text-content-muted"
            >
              Unlocks Live Impact below — the deltas reflect the player leaving
              the squad and don&apos;t change with price.
            </Text>
          </Stack>
          <LiveImpactPanel
            testID="sell-live-impact"
            model={liveImpact}
            onViewFullImpact={onViewFullImpact}
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Stack
          direction="horizontal"
          className="flex-1 flex-wrap items-center justify-between gap-2"
        >
          {isEditMode && onCancelAction != null ? (
            <Button
              variant="link"
              onPress={onCancelAction}
              size="md"
              label="Cancel Sell"
              labelTone="danger"
            />
          ) : (
            <Stack direction="horizontal" />
          )}
          <Stack direction="horizontal" className="gap-4">
            <Button
              variant="outline"
              onPress={onCancel}
              size="md"
              className="border-outline-strong"
              label={isEditMode ? "Close" : "Cancel"}
              labelTone="primary"
            />
            <Button
              onPress={onConfirm}
              size="md"
              className="bg-accent-primary"
              isDisabled={salePrice == null || salePrice <= 0}
              label={isEditMode ? "Update" : "Confirm"}
              labelTone="on-accent"
            />
          </Stack>
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

SellActionModalView.displayName = "SellActionModalView";

export default memo(SellActionModalView);
